import { useState, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";
import Spinner from "./Spinner";

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; company: string; email: string }) => Promise<void>;
  loading?: boolean;
  websiteUrl: string;
  title?: string;
  description?: string;
  companyLabel?: string;
}

interface FormErrors {
  name?: string;
  company?: string;
  email?: string;
}

export default function LeadCaptureModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  websiteUrl,
  title = "Get Your Website Report",
  description,
  companyLabel = "Company",
}: LeadCaptureModalProps) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName("");
      setCompany("");
      setEmail("");
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!company.trim()) {
      newErrors.company = "Company is required";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) {
      return;
    }

    try {
      await onSubmit({ name, company, email });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to submit form. Please try again."
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-strivre-lightnavy">
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 hover:bg-strivre-softgrey rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-left">
          {/* Info Message */}
          <p className="text-sm text-slate-600 leading-relaxed">
            {description || (
              <>
                To access your personalized diagnostics report for{" "}
                <span className="font-semibold text-slate-900">{websiteUrl}</span>, please provide
                your information below.
              </>
            )}
          </p>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              disabled={loading}
              placeholder="Your name"
              className={`input-base w-full ${
                errors.name ? "border-red-500 ring-1 ring-red-500" : ""
              } disabled:bg-slate-50 disabled:cursor-not-allowed`}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.name}
              </p>
            )}
          </div>

          {/* Company Field */}
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-slate-900 mb-2">
              {companyLabel} <span className="text-red-500">*</span>
            </label>
            <input
              id="company"
              type="text"
              value={company}
              onChange={(e) => {
                setCompany(e.target.value);
                if (errors.company) {
                  setErrors({ ...errors, company: undefined });
                }
              }}
              disabled={loading}
              placeholder="Your company"
              className={`input-base w-full ${
                errors.company ? "border-red-500 ring-1 ring-red-500" : ""
              } disabled:bg-slate-50 disabled:cursor-not-allowed`}
            />
            {errors.company && (
              <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.company}
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              disabled={loading}
              placeholder="your.email@example.com"
              className={`input-base w-full ${
                errors.email ? "border-red-500 ring-1 ring-red-500" : ""
              } disabled:bg-slate-50 disabled:cursor-not-allowed`}
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.email}
              </p>
            )}
          </div>

          {/* Submit Error */}
          {submitError && (
            <div className="alert alert-error">
              <AlertCircle size={20} className="flex-shrink-0" />
              <div>
                <p className="font-semibold text-left">Error</p>
                <p className="text-sm">{submitError}</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary btn-md flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-md flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Spinner size="h-4 w-4" />
                  Submitting...
                </>
              ) : (
                "Get Report"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
